import { ISaSmsLedgerEntry, ISaSmsPage } from '@interfaces/sa-sms';
import { Card, Select, Table, Tag } from 'antd';
import { useSaSmsGet } from 'hooks/use-sa-sms-api';
import { useSaSmsUserNames } from 'hooks/use-sa-sms-user-names';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TYPES = [
    'included_grant',
    'included_expire',
    'purchase',
    'auto_recharge',
    'debit',
    'refund',
    'admin_adjust',
];
const PAGE_SIZE = 25;

type Props = { tenantId: string; refreshKey: number };

/** Credit ledger of the tenant, newest first; admin adjustments show their reason and actor. */
export const SmsLedgerTable: React.FC<Props> = ({ tenantId, refreshKey }) => {
    const { t, i18n } = useTranslation(['sa-sms']);
    const [page, setPage] = useState(1);
    const [type, setType] = useState<string>();
    const { data, isLoading, refetch } = useSaSmsGet<ISaSmsPage<ISaSmsLedgerEntry>>(
        `/tenants/${tenantId}/credits/ledger`,
        {
            page,
            pageSize: PAGE_SIZE,
            type,
        },
    );
    // An adjust bumps refreshKey -> reload the page shown.
    useEffect(() => {
        if (refreshKey > 0) refetch();
    }, [refreshKey, refetch]);
    const names = useSaSmsUserNames((data?.items ?? []).map((e) => e.createdBy));
    const delta = (n: number) =>
        n === 0 ? '-' : `${n > 0 ? '+' : ''}${n.toLocaleString(i18n.language)}`;

    return (
        <Card
            title={t('credits.ledger')}
            extra={
                <Select
                    allowClear
                    placeholder={t('credits.allTypes')}
                    style={{ width: 200 }}
                    value={type}
                    onChange={(v) => {
                        setType(v);
                        setPage(1);
                    }}
                    options={TYPES.map((v) => ({ value: v, label: t(`ledgerType.${v}`) }))}
                />
            }
        >
            <Table<ISaSmsLedgerEntry>
                rowKey="id"
                size="small"
                loading={isLoading}
                dataSource={data?.items ?? []}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total: data?.total ?? 0,
                    showSizeChanger: false,
                    onChange: setPage,
                }}
                scroll={{ x: 800 }}
            >
                <Table.Column<ISaSmsLedgerEntry>
                    title={t('credits.date')}
                    render={(_, e) => new Date(e.createdAt).toLocaleString(i18n.language)}
                />
                <Table.Column<ISaSmsLedgerEntry>
                    title={t('credits.type')}
                    render={(_, e) => (
                        <Tag>{t(`ledgerType.${e.type}`, { defaultValue: e.type })}</Tag>
                    )}
                />
                <Table.Column<ISaSmsLedgerEntry>
                    title={t('credits.included')}
                    align="right"
                    render={(_, e) => delta(e.includedDelta)}
                />
                <Table.Column<ISaSmsLedgerEntry>
                    title={t('credits.purchased')}
                    align="right"
                    render={(_, e) => delta(e.purchasedDelta)}
                />
                <Table.Column<ISaSmsLedgerEntry>
                    title={t('credits.after')}
                    align="right"
                    render={(_, e) =>
                        (e.includedAfter + e.purchasedAfter).toLocaleString(i18n.language)
                    }
                />
                <Table.Column<ISaSmsLedgerEntry>
                    title={t('credits.reason')}
                    render={(_, e) => e.meta?.reason ?? '-'}
                />
                <Table.Column<ISaSmsLedgerEntry>
                    title={t('credits.actor')}
                    render={(_, e) => (e.createdBy ? names[e.createdBy] ?? e.createdBy : '-')}
                />
            </Table>
        </Card>
    );
};
